import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  RadioGroupProps,
} from '@mui/material';
import { useController } from 'react-hook-form';
import { useFieldStateError } from './useFieldStateError';
import { ReactNode, useId } from 'react';

export type RadioGroupFieldProps<Value extends string> = RadioGroupProps & {
  name: string;
  label?: ReactNode;
  options: Array<{ label: ReactNode; value: Value }>;
  required?: boolean;
  disabled?: boolean;
};

export function RadioGroupField<Value extends string>({
  name,
  label,
  options,
  required,
  disabled,
  ...props
}: RadioGroupFieldProps<Value>) {
  const id = useId();
  const { field, fieldState } = useController({
    name,
    disabled,
    rules: {
      required,
    },
  });

  const fieldError = useFieldStateError(label, fieldState.error);

  return (
    <FormControl>
      {label ? <FormLabel id={id}>{label}</FormLabel> : null}
      <RadioGroup
        {...props}
        aria-labelledby={id}
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={option.label}
            disabled={field.disabled}
          />
        ))}
      </RadioGroup>
      {fieldError ? <FormHelperText error>{fieldError}</FormHelperText> : null}
    </FormControl>
  );
}
